import { FC, useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, FieldErrors, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Save,
  Cancel,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledAutocomplete from "../../components/controlledComponents/ControlledAutocomplete";
import {
  TSyllabus,
  TSyllabusStatus,
  TSyllabusType,
  TCreateSyllabusRequest,
} from "../../../apis/academic/academic.interfaces";
import {
  useCreateSyllabusHook,
  useUpdateSyllabusHook,
  useSubjectsHook,
  useClassHook,
} from "../../../hooks/useAcademicHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

/** ControlledAutocomplete stores `{ id, label } | null`; API expects optional string IDs. */
const optionalLinkedIdSchema = z
  .union([
    z.object({ id: z.string(), label: z.string().optional() }),
    z.string(),
    z.null(),
    z.undefined(),
  ])
  .transform((v) => {
    if (v == null) return undefined;
    if (typeof v === "string") {
      const t = v.trim();
      return t.length > 0 ? t : undefined;
    }
    if (typeof v === "object" && v !== null && "id" in v) {
      const id = (v as { id: unknown }).id;
      return typeof id === "string" && id.trim().length > 0
        ? id.trim()
        : undefined;
    }
    return undefined;
  });

const curriculumStructureSchema = z
  .object({
    topics: z.array(z.string()).default([]),
    chapters: z.array(z.string()).default([]),
    units: z.array(z.string()).default([]),
    learningObjectives: z.array(z.string()).default([]),
    referenceMaterials: z.array(z.string()).default([]),
  })
  .optional();

const syllabusSchema = z
  .object({
    name: z
      .string()
      .min(1, "Syllabus name is required")
      .min(2, "Syllabus name must be at least 2 characters")
      .max(200, "Syllabus name must not exceed 200 characters"),
    description: z
      .string()
      .max(1000, "Description must not exceed 1000 characters")
      .optional()
      .or(z.literal("")),
    type: z.enum(["course", "class"], {
      errorMap: () => ({ message: "Please select a valid type" }),
    }),
    courseId: optionalLinkedIdSchema,
    classId: optionalLinkedIdSchema,
    academicYear: z
      .string()
      .min(1, "Academic year is required")
      .regex(
        /^\d{4}-\d{4}$/,
        "Academic year must be in format YYYY-YYYY (e.g., 2024-2025)",
      ),
    status: z.enum(["active", "inactive"], {
      errorMap: () => ({ message: "Please select a valid status" }),
    }),
    curriculumStructure: curriculumStructureSchema,
  });

type SyllabusFormValues = z.input<typeof syllabusSchema>;
type SyllabusParsed = z.output<typeof syllabusSchema>;

interface ManageSyllabusDialogProps {
  open: boolean;
  onClose: () => void;
  syllabus?: TSyllabus | null;
}

const ManageSyllabusDialog: FC<ManageSyllabusDialogProps> = ({
  open,
  onClose,
  syllabus,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createSyllabusMutation = useCreateSyllabusHook();
  const updateSyllabusMutation = useUpdateSyllabusHook();
  const { data: subjects } = useSubjectsHook();
  const { data: classes } = useClassHook();
  const isEditMode = !!syllabus;
  const [showCurriculumStructure, setShowCurriculumStructure] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getFieldState,
    formState: { errors, isSubmitting },
  } = useForm<SyllabusFormValues, unknown, SyllabusParsed>({
    resolver: zodResolver(
      syllabusSchema,
    ) as Resolver<SyllabusFormValues, unknown, SyllabusParsed>,
    defaultValues: {
      name: "",
      description: "",
      type: "course",
      courseId: null,
      classId: null,
      academicYear: "",
      status: "active",
      curriculumStructure: {
        topics: [],
        chapters: [],
        units: [],
        learningObjectives: [],
        referenceMaterials: [],
      },
    },
  });

  const watchedType = watch("type");

  const courseOptions = useMemo(
    () =>
      subjects?.map((subject) => ({
        id: subject.id,
        label: subject.name,
      })) ?? [],
    [subjects],
  );

  const classOptions = useMemo(
    () =>
      classes?.map((classItem) => ({
        id: classItem.id,
        label: `${classItem.name} (grade ${classItem.grade})`,
      })) ?? [],
    [classes],
  );

  // @ts-ignore - Type inference issue with optional nested fields in react-hook-form
  const {
    fields: topicFields,
    append: appendTopic,
    remove: removeTopic,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: "curriculumStructure.topics",
  });

  // @ts-ignore - Type inference issue with optional nested fields in react-hook-form
  const {
    fields: chapterFields,
    append: appendChapter,
    remove: removeChapter,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: "curriculumStructure.chapters",
  });

  // @ts-ignore - Type inference issue with optional nested fields in react-hook-form
  // @ts-ignore
  const {
    fields: unitFields,
    append: appendUnit,
    remove: removeUnit,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: "curriculumStructure.units",
  });

  // @ts-ignore - Type inference issue with optional nested fields in react-hook-form
  // @ts-ignore
  const {
    fields: objectiveFields,
    append: appendObjective,
    remove: removeObjective,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: "curriculumStructure.learningObjectives",
  });

  // @ts-ignore - Type inference issue with optional nested fields in react-hook-form
  // @ts-ignore
  const {
    fields: materialFields,
    append: appendMaterial,
    remove: removeMaterial,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: "curriculumStructure.referenceMaterials",
  });

  useEffect(() => {
    if (syllabus) {
      const hasCurriculumStructure = !!(
        syllabus.curriculumStructure?.topics?.length ||
        syllabus.curriculumStructure?.chapters?.length ||
        syllabus.curriculumStructure?.units?.length ||
        syllabus.curriculumStructure?.learningObjectives?.length ||
        syllabus.curriculumStructure?.referenceMaterials?.length
      );
      setShowCurriculumStructure(hasCurriculumStructure);

      reset({
        name: syllabus.name,
        description: syllabus.description || "",
        type: syllabus.type,
        courseId: syllabus.courseId
          ? courseOptions.find((o) => o.id === syllabus.courseId) ?? null
          : null,
        classId: syllabus.classId
          ? classOptions.find((o) => o.id === syllabus.classId) ?? null
          : null,
        academicYear: syllabus.academicYear,
        status: syllabus.status,
        curriculumStructure: syllabus.curriculumStructure || {
          topics: [],
          chapters: [],
          units: [],
          learningObjectives: [],
          referenceMaterials: [],
        },
      });
    } else {
      setShowCurriculumStructure(false);
      reset({
        name: "",
        description: "",
        type: "course",
        courseId: null,
        classId: null,
        academicYear: "",
        status: "active",
        curriculumStructure: {
          topics: [],
          chapters: [],
          units: [],
          learningObjectives: [],
          referenceMaterials: [],
        },
      });
    }
  }, [syllabus, reset, open, courseOptions, classOptions]);

  const typeOptions: { key: TSyllabusType; value: string }[] = [
    { key: "course", value: "Course" },
    { key: "class", value: "Class" },
  ];

  const statusOptions: { key: TSyllabusStatus; value: string }[] = [
    { key: "active", value: "Active" },
    { key: "inactive", value: "Inactive" },
  ];

  const handleCreateSyllabus = async (request: TCreateSyllabusRequest) => {
    await createSyllabusMutation.mutateAsync(request);
    logger.show_success("Syllabus created successfully!");
  };

  const firstFormErrorMessage = (fieldErrors: unknown): string | undefined => {
    if (!fieldErrors || typeof fieldErrors !== "object") return undefined;
    if (Array.isArray(fieldErrors)) {
      for (const item of fieldErrors) {
        const msg = firstFormErrorMessage(item);
        if (msg) return msg;
      }
      return undefined;
    }
    const record = fieldErrors as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.length > 0) {
      return record.message;
    }
    for (const value of Object.values(record)) {
      const msg = firstFormErrorMessage(value);
      if (msg) return msg;
    }
    return undefined;
  };

  const onInvalid = (validationErrors: FieldErrors) => {
    logger.show_error(
      firstFormErrorMessage(validationErrors) ??
        "Please fix the highlighted fields before saving.",
    );
  };

  const onSubmit = async (data: SyllabusParsed) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before creating or editing a syllabus.",
      );
      return;
    }

    try {
      const curriculumStructure = showCurriculumStructure
        ? {
          topics: data.curriculumStructure?.topics?.filter(Boolean) || [],
          chapters: data.curriculumStructure?.chapters?.filter(Boolean) || [],
          units: data.curriculumStructure?.units?.filter(Boolean) || [],
          learningObjectives:
            data.curriculumStructure?.learningObjectives?.filter(Boolean) ||
            [],
          referenceMaterials:
            data.curriculumStructure?.referenceMaterials?.filter(Boolean) ||
            [],
        }
        : undefined;

      if (isEditMode && syllabus) {
        await updateSyllabusMutation.mutateAsync({
          id: syllabus.id,
          request: {
            name: data.name,
            description: data.description || undefined,
            type: data.type,
            courseId: data.courseId,
            classId: data.classId,
            academicYear: data.academicYear,
            curriculumStructure,
            status: data.status,
          },
        });
        logger.show_success("Syllabus updated successfully!");
      } else {
        await handleCreateSyllabus({
          instituteId: selectedInstitute.id,
          name: data.name,
          description: data.description || undefined,
          type: data.type,
          courseId: data.courseId,
          classId: data.classId,
          academicYear: data.academicYear,
          curriculumStructure,
          status: data.status,
        });
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} syllabus. Please try again.`,
      );
    }
  };

  const isLoading =
    isSubmitting ||
    createSyllabusMutation.isPending ||
    updateSyllabusMutation.isPending;
  const isError =
    createSyllabusMutation.isError || updateSyllabusMutation.isError;
  const errorMessage =
    createSyllabusMutation.error?.message ||
    updateSyllabusMutation.error?.message;

  const renderArrayField = (
    fields: any[],
    append: () => void,
    remove: (index: number) => void,
    label: string,
    fieldName:
      | "curriculumStructure.topics"
      | "curriculumStructure.chapters"
      | "curriculumStructure.units"
      | "curriculumStructure.learningObjectives"
      | "curriculumStructure.referenceMaterials",
    testIdPrefix: string,
  ) => {
    return (
      <Grid item xs={12} data-testid={`${testIdPrefix}-container`}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight="medium">
            {label}
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={append}
            data-testid={`add-${testIdPrefix}-button`}
          >
            Add {label}
          </Button>
        </Box>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            display="flex"
            gap={1}
            sx={{ mb: 1 }}
            data-testid={`${testIdPrefix}-item-${index}`}
          >
            <ControlledTextField
              control={control as any}
              name={`${fieldName}.${index}`}
              id={`${testIdPrefix}-input-${index}`}
              label=""
              isGridItem={false}
              fullWidth
              errorMessage={getFieldState(`${fieldName}.${index}`).error?.message}
              inputProps={{ "data-testid": `${testIdPrefix}-input-${index}` }}
            />
            <IconButton
              size="small"
              onClick={() => remove(index)}
              color="error"
              data-testid={`remove-${testIdPrefix}-button-${index}`}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-syllabus-dialog"
    >
      <DialogTitle data-testid="manage-syllabus-dialog-title">
        {isEditMode ? "Edit Syllabus" : "Add New Syllabus"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        data-testid="syllabus-form"
      >
        <DialogContent data-testid="syllabus-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="syllabus-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} syllabus. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="syllabus-form-container"
          >
            <ControlledTextField
              control={control as any}
              name="name"
              id="syllabus-name"
              label="Syllabus Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "syllabus-name-input" }}
            />

            <ControlledSelectField
              control={control as any}
              name="type"
              id="syllabus-type"
              label="Type"
              itemList={typeOptions}
              isRequired
              errorMessage={errors.type?.message}
            />

            {watchedType === "course" ? (
              <>
                {!subjects?.length ? (
                  <Grid item xs={12}>
                    <Alert severity="info" data-testid="no-courses-message">
                      Add subjects under Subjects to link one here, or leave
                      unlinked. Type &quot;Class&quot; links a class instead.
                    </Alert>
                  </Grid>
                ) : null}
                <ControlledAutocomplete
                  control={control as any}
                  name="courseId"
                  id="syllabus-course"
                  label="Subject (optional)"
                  dataOptions={courseOptions}
                  disabled={!subjects?.length}
                  errorMessage={errors.courseId?.message}
                  dataTestId="syllabus-course-input"
                />
              </>
            ) : (
              <ControlledAutocomplete
                control={control as any}
                name="classId"
                id="syllabus-class"
                label="Associated Class (optional)"
                dataOptions={classOptions}
                errorMessage={errors.classId?.message}
                dataTestId="syllabus-class-input"
              />
            )}

            <ControlledTextField
              control={control as any}
              name="academicYear"
              id="syllabus-academic-year"
              label="Academic Year"
              placeholder="2024-2025"
              required
              errorMessage={errors.academicYear?.message}
              inputProps={{ "data-testid": "syllabus-academic-year-input" }}
            />

            <ControlledSelectField
              control={control as any}
              name="status"
              id="syllabus-status"
              label="Status"
              itemList={statusOptions}
              isRequired
              errorMessage={errors.status?.message}
            />

            <Grid item xs={12}>
              <ControlledTextField
                control={control as any}
                name="description"
                id="syllabus-description"
                label="Description"
                multiline
                rows={3}
                errorMessage={errors.description?.message}
                inputProps={{ "data-testid": "syllabus-description-input" }}
                isGridItem={false}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Curriculum Structure</Typography>
                <Button
                  size="small"
                  onClick={() =>
                    setShowCurriculumStructure(!showCurriculumStructure)
                  }
                  data-testid="toggle-curriculum-structure-button"
                >
                  {showCurriculumStructure ? "Hide" : "Show"} Structure
                </Button>
              </Box>
            </Grid>

            {showCurriculumStructure && (
              <>
                {renderArrayField(
                  topicFields,
                  () => appendTopic(""),
                  removeTopic,
                  "Topics",
                  "curriculumStructure.topics",
                  "syllabus-topic",
                )}
                {renderArrayField(
                  chapterFields,
                  () => appendChapter(""),
                  removeChapter,
                  "Chapters",
                  "curriculumStructure.chapters",
                  "syllabus-chapter",
                )}
                {renderArrayField(
                  unitFields,
                  () => appendUnit(""),
                  removeUnit,
                  "Units",
                  "curriculumStructure.units",
                  "syllabus-unit",
                )}
                {renderArrayField(
                  objectiveFields,
                  () => appendObjective(""),
                  removeObjective,
                  "Learning Objectives",
                  "curriculumStructure.learningObjectives",
                  "syllabus-objective",
                )}
                {renderArrayField(
                  materialFields,
                  () => appendMaterial(""),
                  removeMaterial,
                  "Reference Materials",
                  "curriculumStructure.referenceMaterials",
                  "syllabus-material",
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions data-testid="syllabus-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-syllabus-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-syllabus-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Syllabus"
                : "Create Syllabus"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageSyllabusDialog;
